import { toPng } from "html-to-image";
import { toast } from "sonner";
import { ArrowRight, Download, Share2, Shield, Snowflake, Sparkles } from "lucide-react";
import Modal from "@/components/Modal";

/**
 * StreakSavedCelebration — shown after a save-token spend succeeds.
 * Renders a shareable "I rescued my streak" card that can be downloaded
 * or shared with a referral link attached.
 */
export default function StreakSavedCelebration({
  celebration, onClose, cardRef, exporting, setExporting, referralCode, userName,
}) {
  if (!celebration) return null;
  const link = referralCode
    ? `${window.location.origin}/?ref=${referralCode}`
    : window.location.origin;

  const generatePng = async () => {
    if (!cardRef.current) return null;
    setExporting(true);
    try {
      return await toPng(cardRef.current, {
        cacheBust: true,
        pixelRatio: 2,
        backgroundColor: "#0f2419",
        skipFonts: true,
      });
    } catch (e) {
      console.error(e);
      toast.error("Export failed");
      return null;
    } finally {
      setExporting(false);
    }
  };

  const download = async () => {
    const url = await generatePng();
    if (!url) return;
    const a = document.createElement("a");
    a.href = url;
    a.download = `streak-saved-${celebration.year}-${celebration.month_short}.png`;
    a.click();
    toast.success("Card downloaded");
  };

  const share = async () => {
    const url = await generatePng();
    if (!url) return;
    const shareText = `I just rescued my savings streak in ${celebration.month_short} ${celebration.year} — back to ${celebration.streak_after} months. Try Personal Finance Optimizer: ${link}`;
    try {
      const blob = await (await fetch(url)).blob();
      const file = new File([blob], `streak-saved-${celebration.year}-${celebration.month_short}.png`, { type: "image/png" });
      if (navigator.canShare?.({ files: [file] })) {
        await navigator.share({ title: "Streak saved!", text: shareText, files: [file] });
      } else {
        await navigator.clipboard.writeText(shareText);
        toast.success("Highlight + referral link copied");
      }
    } catch (e) {
      if (e?.name !== "AbortError") toast.error("Share failed");
    }
  };

  return (
    <Modal open={!!celebration} onClose={onClose} title="Streak saved" size="lg">
      <div className="grid gap-5">
        <div className="flex justify-center">
          <div
            ref={cardRef}
            data-testid="streak-saved-card"
            className="relative overflow-hidden"
            style={{
              width: 620,
              maxWidth: "100%",
              background: "linear-gradient(160deg, #0f2419 0%, #21422F 50%, #2D5A40 100%)",
              color: "#FDFCFB",
              borderRadius: 20,
              padding: 36,
              boxShadow: "0 30px 60px -20px rgba(0,0,0,0.55)",
              fontFamily: "var(--font-body)",
            }}
          >
            <div className="absolute -top-20 -right-16 h-72 w-72 rounded-full pointer-events-none"
                 style={{ background: "radial-gradient(circle, rgba(134,168,216,0.42) 0%, rgba(134,168,216,0) 70%)" }}/>
            <div className="absolute -bottom-24 -left-12 h-56 w-56 rounded-full pointer-events-none"
                 style={{ background: "radial-gradient(circle, rgba(224,158,80,0.22) 0%, rgba(224,158,80,0) 70%)" }}/>
            <div className="absolute inset-0 pointer-events-none opacity-30"
                 style={{ backgroundImage: "radial-gradient(rgba(255,255,255,0.08) 1px, transparent 1px)", backgroundSize: "3px 3px" }}/>
            <div className="relative">
              <div className="flex items-center gap-2" style={{ opacity: 0.75 }}>
                <Shield size={16} color="#86A8D8"/>
                <div style={{ fontSize: 11, letterSpacing: "0.28em", textTransform: "uppercase" }}>Streak Saved</div>
              </div>
              <div style={{ marginTop: 18, display: "flex", alignItems: "center", gap: 18 }}>
                <div style={{
                  height: 84, width: 84, borderRadius: 22,
                  background: "linear-gradient(135deg, #86A8D8, #6B8FBB)",
                  display: "flex", alignItems: "center", justifyContent: "center",
                  boxShadow: "0 12px 28px -10px rgba(107,143,187,0.6)",
                }}>
                  <Snowflake size={44} color="#fff"/>
                </div>
                <div>
                  <div style={{ fontFamily: "var(--font-heading)", fontSize: 32, fontWeight: 600, letterSpacing: "-0.02em", lineHeight: 1.05 }}>
                    I rescued my streak
                  </div>
                  <div style={{ marginTop: 4, fontSize: 15, opacity: 0.8 }}>
                    in <span style={{ color: "#E09E50", fontWeight: 600 }}>{celebration.month_short} {celebration.year}</span>
                  </div>
                </div>
              </div>
              <div style={{ marginTop: 28, display: "grid", gridTemplateColumns: "1fr auto 1fr", gap: 16, alignItems: "center" }}>
                <div style={{ background: "rgba(0,0,0,0.22)", borderRadius: 14, padding: 16, textAlign: "center" }}>
                  <div style={{ fontSize: 10, letterSpacing: "0.22em", opacity: 0.65, textTransform: "uppercase" }}>Before</div>
                  <div style={{ fontFamily: "var(--font-mono)", fontSize: 40, fontWeight: 500, marginTop: 4, letterSpacing: "-0.02em", lineHeight: 1 }}>{celebration.streak_before}</div>
                  <div style={{ fontSize: 11, opacity: 0.7, marginTop: 3 }}>month{celebration.streak_before === 1 ? "" : "s"}</div>
                </div>
                <ArrowRight size={22} style={{ opacity: 0.6 }}/>
                <div style={{ background: "linear-gradient(135deg, rgba(224,158,80,0.35), rgba(224,158,80,0.15))", borderRadius: 14, padding: 16, textAlign: "center", border: "1px solid rgba(224,158,80,0.4)" }}>
                  <div style={{ fontSize: 10, letterSpacing: "0.22em", opacity: 0.85, textTransform: "uppercase" }}>After</div>
                  <div style={{ fontFamily: "var(--font-mono)", fontSize: 44, fontWeight: 500, marginTop: 4, letterSpacing: "-0.02em", lineHeight: 1, color: "#E09E50" }} data-testid="celebration-streak-after">{celebration.streak_after}</div>
                  <div style={{ fontSize: 11, opacity: 0.85, marginTop: 3 }}>month{celebration.streak_after === 1 ? "" : "s"}</div>
                </div>
              </div>
              <div style={{ marginTop: 24, padding: "10px 14px", background: "rgba(255,255,255,0.07)", borderRadius: 12, border: "1px solid rgba(255,255,255,0.1)", display: "flex", alignItems: "center", gap: 10 }}>
                <Sparkles size={14} color="#E09E50"/>
                <div style={{ fontSize: 13 }}>
                  One save token spent. Back on track — {userName?.split(" ")[0] || "friend"}.
                </div>
              </div>
              <div style={{ marginTop: 22, display: "flex", justifyContent: "space-between", alignItems: "center", fontSize: 11, opacity: 0.7 }}>
                <div style={{ letterSpacing: "0.18em", textTransform: "uppercase" }}>Personal Finance Optimizer</div>
                <div style={{ fontFamily: "var(--font-mono)" }}>{window.location.host}</div>
              </div>
            </div>
          </div>
        </div>

        <p className="text-sm text-[var(--text-secondary)] text-center leading-relaxed">
          Share your comeback — you inspire others (and each new signup via your link gives you an extra save token).
        </p>

        <div className="flex justify-center gap-2 flex-wrap">
          <button className="btn-ghost" onClick={download} disabled={exporting} data-testid="celebration-download-btn">
            <Download size={15}/> {exporting ? "Rendering…" : "Download"}
          </button>
          <button className="btn-primary" onClick={share} disabled={exporting} data-testid="celebration-share-btn">
            <Share2 size={15}/> Share with referral link
          </button>
          <button className="btn-ghost" onClick={onClose} data-testid="celebration-close-btn">
            Done
          </button>
        </div>
      </div>
    </Modal>
  );
}
